import React, { useMemo, memo, useEffect } from 'react';
import { signOut } from 'next-auth/react';
import { M200ProfileModule } from '~/lib/data-contract';
import { MenuModuleContainer } from '../MenuModuleContainer';
import { RawHtml, SkeletonLegacy } from '~/shared/components';
import { useFrame, useTranslation } from '~/shared/utils';
import {
    StyledContainer,
    StyledContent,
    StyledInactiveContent,
    StyledMenuButton,
    StyledMenuLink,
    StyledOrderHistoryLink,
} from './styled';
import { useAccount } from '~/features/club';
import { useUIState } from './hooks/useUIState';
import { DeleteUserSection, Sections } from './components';
import { Spacer } from '~/theme';
import OrderHistory from './order-history/order-history.component';
import { useRouter } from 'next/router';

export type M200ProfileProps = M200ProfileModule;

const ORDER_HISTORY_SECTION_ID = 'order-history';

export const M200Profile = memo(({ inactiveAccountContent }: M200ProfileProps) => {
    const [showOrderHistory, setShowOrderHistory] = React.useState(false);
    const { data: frame } = useFrame();
    const { translate } = useTranslation();
    const { account } = useAccount();
    const { activeId, sections } = useUIState();
    const callbackUrl = frame?.staticLinks?.frontPage?.url || '/';
    const { orderHistory: showOrderHistoryFrameSetting } = frame?.settings?.features || {};
    const { inactive } = account || {};

    const router = useRouter();

    useEffect(() => {
        const handleRouteChange = () => {
            if (window.location.hash === `#${ORDER_HISTORY_SECTION_ID}`) {
                setShowOrderHistory(true);
            } else {
                setShowOrderHistory(false);
            }
        };

        // Check on initial load
        handleRouteChange();

        // Listen for route changes
        router.events.on('hashChangeComplete', handleRouteChange);
        router.events.on('routeChangeComplete', handleRouteChange);

        return () => {
            router.events.off('hashChangeComplete', handleRouteChange);
            router.events.off('routeChangeComplete', handleRouteChange);
        };
    }, [router]);

    const menuItems = useMemo(
        () =>
            sections.map(({ translation, domId, id }) => (
                <StyledMenuLink
                    key={id}
                    href={`#${domId}`}
                    bold={activeId === id && !showOrderHistory}
                    disabled={inactive || false}
                    showHoverIndicator={false}
                    shallow={true}
                    scroll={false}
                    content={translation}
                    onClick={() => setShowOrderHistory(false)}
                >
                    {translation}
                </StyledMenuLink>
            )),
        [sections, activeId, inactive, showOrderHistory]
    );

    return (
        <StyledContainer>
            <MenuModuleContainer
                menu={
                    account
                        ? [
                              ...menuItems,
                              ...(showOrderHistoryFrameSetting ? [
                                  <StyledOrderHistoryLink
                                      href={`#${ORDER_HISTORY_SECTION_ID}`}
                                      key={100}
                                      showHoverIndicator={false}
                                      bold={showOrderHistory}
                                      onClick={() => {
                                          window.scrollTo({ top: 0, behavior: 'smooth' });
                                      }}
                                  >
                                      {translate('club.orderHistory.headline')}
                                  </StyledOrderHistoryLink>
                              ] : []),
                              <StyledMenuButton
                                  key={99}
                                  onClick={() => signOut({ callbackUrl })}
                                  children={translate('club.profile.signOut')}
                              />,
                          ]
                        : [
                              <SkeletonLegacy key={1} style={{ width: '100%', height: '25px' }} />,
                              <SkeletonLegacy key={2} style={{ width: '100%', height: '25px' }} />,
                              <SkeletonLegacy key={3} style={{ width: '100%', height: '25px' }} />,
                              <SkeletonLegacy key={4} style={{ width: '100%', height: '25px' }} />,
                              <SkeletonLegacy key={5} style={{ width: '100%', height: '25px' }} />,
                              <SkeletonLegacy key={6} style={{ width: '100%', height: '25px' }} />,
                              <SkeletonLegacy key={7} style={{ width: '100%', height: '25px' }} />,
                          ]
                }
            >
                {!account ? (
                    <>
                        <StyledContent>{translate('club.profile.pending')}</StyledContent>
                        <br />
                        <SkeletonLegacy style={{ width: '1fr', height: '100vh' }} />
                    </>
                ) : null}

                {account?.inactive ? (
                    <>
                        {inactiveAccountContent && (
                            <StyledInactiveContent>
                                <RawHtml html={inactiveAccountContent} />
                                <Spacer space="4" />
                                <DeleteUserSection />
                            </StyledInactiveContent>
                        )}
                    </>
                ) : null}

                {account && !account.inactive ? (
                    <StyledContent>
                        {(showOrderHistory && showOrderHistoryFrameSetting) ? <OrderHistory /> : <Sections />}
                    </StyledContent>
                ) : null}
            </MenuModuleContainer>
        </StyledContainer>
    );
});
